import { action, computed, observable, reaction } from 'mobx';
import uuidv4 from 'uuid/v4';
import { Template, TemplatesResponse } from '../../../types/api';
import { ApiClient, ApiClientParams } from '../apiClient/ApiClient';
import { ErrorReporter } from '../errorReporter/ErrorReporter';
import { ExperimentsStore } from '../../stores/ExperimentsStore';
import { AppName, Apps, APPS_BY_FEATURE } from './appDefIdsByFeature';
import { BaseURLStore } from './BaseURLStore';
import { CategoriesStore } from './CategoriesStore';
import { FilterCollection } from './routes/sortFiltersParams';
import { filtersCount, isFiltersEmpty, MatchedRoute, RoutingStore } from './RoutingStore';
import { InteractionType, UiLoggerStore } from './UiLoggerStore';
import { FedopsLoggerStore } from './FedopsLoggerStore';
import { RedirectStore } from './RedirectStore';
import { HeaderStore } from './HeaderStore';

export type ItemsPerPage = [number, Template[]][];
export const ITEMS_PER_PAGE_WITH_WIDGET = 10;
export const ITEMS_PER_PAGE = 12;

export interface TemplatesStoreState {
  itemsTotal: number;
  itemsPerPage: ItemsPerPage;
  hasMore: boolean;
  searchId?: string;
  loadedRoute: MatchedRoute | null;
}

export enum WidgetType {
  All = 'all',
  OnlineStore = 'online-store',
  Blog = 'blog',
  Photography = 'photography',
  Events = 'events',
  Business = 'business',
}

interface TemplatesStoreParams {
  experimentsStore: ExperimentsStore;
  routingStore: RoutingStore;
  redirectStore: RedirectStore;
  categoriesStore: CategoriesStore;
  apiClient: ApiClient;
  errorReporter: ErrorReporter;
  apps: Apps;
  baseURLStore: BaseURLStore;
  uiLoggerStore: UiLoggerStore;
  fedopsLoggerStore: FedopsLoggerStore;
  headerStore: HeaderStore;
}

type FetchCategoryParams = {
  type: 'category';
  criteria: null;
  categorySlug: string;
  subCategorySlug: string | null;
  page: number;
  filters: FilterCollection;
  sortCategorySlug: string | null;
};

type FetchSearchParams = {
  type: 'search';
  criteria: string;
  categorySlug: null;
  subCategorySlug: null;
  page: number;
  filters: FilterCollection;
  sortCategorySlug: string | null;
};

export type TemplateStoreFetchParams = FetchCategoryParams | FetchSearchParams;

export class TemplatesStore {
  private readonly experimentsStore: ExperimentsStore;
  private readonly routingStore: RoutingStore;
  private readonly redirectStore: RedirectStore;
  private readonly categoriesStore: CategoriesStore;
  private readonly apiClient: ApiClient;
  private readonly errorReporter: ErrorReporter;
  private readonly uiLoggerStore: UiLoggerStore;
  private readonly fedopsLoggerStore: FedopsLoggerStore;
  private readonly headerStore: HeaderStore;
  public readonly apps: Apps;
  private readonly baseURLStore: BaseURLStore;
  @observable itemsPerPageUUID: string = uuidv4();

  @observable viewedTemplateIds: string[] = [];

  @observable
  public isLoadingTemplates: boolean = false;

  @observable
  public itemsPerPage: Map<number, Template[]> = new Map();

  @observable
  public itemsTotal: number = 0;

  @observable
  public hasMore: boolean = false;

  @observable
  public searchId?: string;

  @observable
  private loadedRoute: MatchedRoute | null = null;

  @computed
  public get loadedFetchParams(): TemplateStoreFetchParams | null {
    return this.matchedRouteToFetchParams(this.loadedRoute);
  }

  @computed
  public get isLoaded(): boolean {
    return !!this.loadedRoute;
  }

  @computed
  public get fetchParams(): TemplateStoreFetchParams | null {
    return this.matchedRouteToFetchParams(this.routingStore.matchedRoute);
  }

  constructor(
    {
      experimentsStore,
      routingStore,
      redirectStore,
      categoriesStore,
      apiClient,
      errorReporter,
      apps,
      baseURLStore,
      uiLoggerStore,
      fedopsLoggerStore,
      headerStore,
    }: TemplatesStoreParams,
    initialState?: TemplatesStoreState,
  ) {
    this.experimentsStore = experimentsStore;
    this.routingStore = routingStore;
    this.redirectStore = redirectStore;
    this.categoriesStore = categoriesStore;
    this.apiClient = apiClient;
    this.errorReporter = errorReporter;
    this.apps = apps;
    this.baseURLStore = baseURLStore;
    this.uiLoggerStore = uiLoggerStore;
    this.fedopsLoggerStore = fedopsLoggerStore;
    this.headerStore = headerStore;

    if (initialState) {
      this.hasMore = initialState.hasMore;
      this.itemsTotal = initialState.itemsTotal;
      this.itemsPerPage = new Map<number, Template[]>(initialState.itemsPerPage);
      this.searchId = initialState.searchId;
      this.loadedRoute = initialState.loadedRoute;
    }

    this.listenForRouteChanges();
  }

  public async initialFetch() {
    await this.fetch(this.routingStore.matchedRoute);
  }

  public serialize(): TemplatesStoreState {
    const { itemsPerPage, itemsTotal, hasMore, loadedRoute } = this;
    return { itemsPerPage: Array.from(itemsPerPage.entries()), itemsTotal, hasMore, loadedRoute };
  }

  @action
  public setItems(
    {
      page,
      hasMore,
      items,
      totalItems,
      searchId,
      loadedRoute,
    }: {
      page: number;
      items: Template[];
      totalItems: number;
      hasMore: boolean;
      searchId?: string;
      loadedRoute: MatchedRoute;
    },
    reset: boolean = false,
  ) {
    if (reset) {
      this.itemsPerPage = new Map();
      this.resetViewedTemplateIds();
      this.itemsPerPageUUID = uuidv4();
    }
    this.itemsPerPage.set(page, items);
    this.itemsTotal = totalItems;
    this.hasMore = hasMore;
    this.searchId = searchId;
    this.loadedRoute = loadedRoute;
  }

  @action
  private setLoadingTemplates(isLoading: boolean = false) {
    this.isLoadingTemplates = isLoading;
  }

  @computed
  public get loadedItemsCount(): number {
    return Array.from(this.itemsPerPage.values()).reduce((total, perPage) => total + perPage.length, 0);
  }

  @computed
  private get isOnlyPageNumberChanged() {
    const matchedRoute: MatchedRoute = this.routingStore.matchedRoute;
    if (
      !this.routingStore.previousMatchedRoute ||
      matchedRoute.routeName === 'home' ||
      matchedRoute.routeName === 'forYou'
    ) {
      return false;
    }

    if (this.routingStore.previousMatchedRoute.routeName === 'home' && matchedRoute.routeName !== 'search') {
      return (
        matchedRoute.params.categorySlug === this.routingStore.primaryCategorySlug && matchedRoute.params.page === 2
      );
    }

    const prevRoute = JSON.parse(JSON.stringify(this.routingStore.previousMatchedRoute));
    const isSamePage = prevRoute.params.page === matchedRoute.params.page;
    prevRoute.params.page = matchedRoute.params.page;
    return JSON.stringify(matchedRoute) === JSON.stringify(prevRoute) && !isSamePage;
  }

  public getInteractionType(): InteractionType {
    let interactionType: InteractionType;
    if (this.routingStore.matchedRoute.routeName === 'search') {
      interactionType = 'search';
    } else if (this.isOnlyPageNumberChanged) {
      interactionType = 'pagination';
    } else {
      interactionType = 'category_switch';
    }

    return interactionType;
  }

  public getPreviousUrl(): string | undefined {
    return this.routingStore.previousLocation
      ? this.baseURLStore.buildURL(this.routingStore.previousLocation)
      : undefined;
  }

  private listenForRouteChanges() {
    reaction(
      () => this.routingStore.matchedRoute,
      (matchedRoute) => this.fetch(matchedRoute, this.getPreviousUrl()),
    );
  }

  public getWidgetType(params: TemplateStoreFetchParams | null): WidgetType | null {
    if (!params || params.type === 'search') {
      return null;
    }

    if (
      this.experimentsStore.is('specs.funnel.TemplatesGalleryEnableWidget', 'Disabled') &&
      this.experimentsStore.is('specs.funnel.TemplatesGalleryEnableWidgetForLoggedInUsers', 'Disabled')
    ) {
      return null;
    }

    const { categorySlug, subCategorySlug, page } = params;
    const isFirstPage =
      page === 1 || this.experimentsStore.is('specs.funnel.TemplatesGalleryV2NumericPagination', 'false');
    const isAnyFiltersApplied = filtersCount(params.filters) > 0;

    if (subCategorySlug || !isFirstPage || isAnyFiltersApplied) {
      return null;
    }

    return Object.values(WidgetType).includes(categorySlug as WidgetType) ? (categorySlug as WidgetType) : null;
  }

  private async fetch(matchedRoute: MatchedRoute, previousUrl?: string): Promise<void> {
    const fetchParams = this.matchedRouteToFetchParams(matchedRoute);
    if (!fetchParams || this.redirectStore.redirect !== null) {
      return;
    }

    this.setLoadingTemplates(true);
    let response: TemplatesResponse | null = null;

    try {
      const interactionType = this.getInteractionType();
      const interaction = this.uiLoggerStore.initInteraction(interactionType);
      this.uiLoggerStore.logStartedLoadingThePage(interaction.key);

      if (interactionType === 'category_switch') {
        this.fedopsLoggerStore.startCategoryInteraction();
      }
      if (fetchParams.type === 'category') {
        response = await this.browseTemplates(fetchParams, previousUrl);
      } else {
        response = await this.searchTemplates(fetchParams, previousUrl);
        this.uiLoggerStore.logSearch({
          criteria: fetchParams.criteria,
          totalPages: response.pagination.total,
          currentPage: response.pagination.current,
          numberOfTemplatesInCurrentPage: response.itemsTotal,
          source: 'search-box',
          searchId: response.searchId,
        });
      }

      this.uiLoggerStore.logFinishedLoadingThePage(interaction.key);
    } catch (error) {
      this.errorReporter.reportError(error);
    }
    response && this.setTemplates(response, matchedRoute);
    this.setLoadingTemplates(false);
  }

  private matchedRouteToFetchParams(matchedRoute: MatchedRoute | null): TemplateStoreFetchParams | null {
    if (!matchedRoute || matchedRoute.routeName === 'forYou') {
      return null;
    }

    if (matchedRoute.routeName === 'home') {
      return {
        type: 'category',
        criteria: null,
        categorySlug: this.routingStore.primaryCategorySlug,
        subCategorySlug: null,
        page: 1,
        filters: { features: [], colors: [], colorStyles: [], layouts: [] },
        sortCategorySlug: null,
      };
    }

    const { page, filters, sortCategorySlug } = matchedRoute.params;
    if (matchedRoute.routeName === 'search') {
      return {
        type: 'search' as const,
        criteria: matchedRoute.params.criteria,
        categorySlug: null,
        subCategorySlug: null,
        page,
        filters,
        sortCategorySlug,
      };
    }

    return {
      type: 'category' as const,
      criteria: null,
      categorySlug: matchedRoute.params.categorySlug,
      subCategorySlug: matchedRoute.routeName === 'subCategory' ? matchedRoute.params.subCategorySlug : null,
      page,
      filters,
      sortCategorySlug,
    };
  }

  private getPageOffsetLimit(params: TemplateStoreFetchParams): { offset: number; limit: number } {
    const pagesOffset = Math.max(0, params.page - 1);
    const firstPageLimit =
      this.getWidgetType({ ...params, page: 1 }) !== null ? ITEMS_PER_PAGE_WITH_WIDGET : ITEMS_PER_PAGE;
    const offset = pagesOffset === 0 ? 0 : firstPageLimit + (pagesOffset - 1) * ITEMS_PER_PAGE;
    const limit = params.page === 1 ? firstPageLimit : ITEMS_PER_PAGE;
    return { offset, limit };
  }

  private searchTemplates(params: FetchSearchParams, previousUrl?: string): Promise<TemplatesResponse> {
    const { criteria, filters, page, sortCategorySlug } = params;

    const [sortCategoryId] = sortCategorySlug ? this.categoriesStore.getCategoryIds([sortCategorySlug]) : [];

    return this.apiClient.search(
      {
        ...this.getPageOffsetLimit(params),
        criteria,
        sortCategoryId,
        filters: this.buildApiFilters(filters),
        templatePageSessionId: this.uiLoggerStore.templatePageSessionId,
        biData: { page },
      },
      previousUrl,
    );
  }

  private browseTemplates(params: FetchCategoryParams, previousUrl?: string): Promise<TemplatesResponse> {
    const { categorySlug, subCategorySlug, filters, page, sortCategorySlug } = params;
    const categoryIds = this.categoriesStore.getCategoryIds([subCategorySlug || categorySlug]);
    const [sortCategoryId] = sortCategorySlug ? this.categoriesStore.getCategoryIds([sortCategorySlug]) : [];

    return this.apiClient.browse(
      {
        ...this.getPageOffsetLimit(params),
        categoryIds,
        sortCategoryId,
        filters: this.buildApiFilters(filters),
        templatePageSessionId: this.uiLoggerStore.templatePageSessionId,
        biData: {
          page,
          bookName: this.categoriesStore.bookName,
          category: this.routingStore.activeCategorySlug,
          subCategory: this.routingStore.activeSubCategorySlug,
          criteria: this.headerStore.searchCriteria.trim(),
          isForYouPage: false,
        },
      },
      previousUrl,
    );
  }

  @action
  private setTemplates(
    { pagination, itemsTotal, items, searchId }: TemplatesResponse,
    loadedRoute: MatchedRoute,
  ): void {
    this.setItems(
      {
        page: pagination.current,
        items,
        totalItems: itemsTotal,
        hasMore: pagination.current < pagination.total,
        searchId,
        loadedRoute,
      },
      !this.isOnlyPageNumberChanged || this.experimentsStore.templatesGalleryV2NumericPagination,
    );
  }

  private buildApiFilters(filters: FilterCollection): ApiClientParams['filters'] {
    const appNamesByFeature = filters.features.map((feature) => APPS_BY_FEATURE[feature]);
    const appNames: AppName[] = [].concat(...appNamesByFeature).filter(Boolean);

    return {
      apps: appNames.map((appName) => this.apps[appName]),
      features: filters.features,
      colors: filters.colors,
      colorStyles: filters.colorStyles,
      layouts: filters.layouts,
    };
  }

  public wasTemplateInView(id: string): boolean {
    return this.viewedTemplateIds.includes(id);
  }

  @action
  public resetViewedTemplateIds() {
    this.viewedTemplateIds = [];
  }

  @action
  public addViewedTemplateId(id: string) {
    this.viewedTemplateIds.push(id);
  }

  @computed
  public get isNoResultSuggestions(): boolean {
    return Array.from(this.itemsPerPage.values()).every((templates: Template[]) => {
      return templates.every((it) => it.source === 'NoResultSuggestion');
    });
  }

  @computed
  public get isNoFiltersResult(): boolean {
    return (
      !isFiltersEmpty(this.loadedFetchParams?.filters) && (this.loadedItemsCount === 0 || this.isNoResultSuggestions)
    );
  }

  @computed
  public get totalPages(): number {
    return Math.ceil(this.itemsTotal / 12);
  }
}
